import { TankPlanCondition } from 'src/enums/stowage/tankPlanCondition';
import { TankContent } from 'src/types/ViewModel/Stowage/Manifest/TankContent';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import {
    getLastBerthCallInPortCall,
    getPreviousPortCall,
    getBerthCallForSelection,
    getPreviousBerthCall,
    getLastBerthCallFromPrecedingPortCalls,
} from '../stowageRotationUtils';
import {
    getTankContentInBerthCall,
    getFilteredTankContent,
    getTankContentsInPortCall,
    getFilteredTankContents,
    getCargoContentsInBerthCall,
    getFilteredCargoContents,
    getCargoContentsInPortCall,
} from './cargoManifestUtils';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { isPositive } from '../decimalsUtils';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import Decimal from 'decimal.js';

const getTankContentForOperationsInternal = (
    stowagePlan: StowagePlan,
    selectedBerthCall: RotationBerthCall,
    selectedPortCall: RotationPortCall,
    tank: Tank,
    operationTypes: string[]
): TankContent | null => {
    return selectedBerthCall
        ? getFilteredTankContent(getTankContentInBerthCall(stowagePlan, selectedBerthCall, tank), operationTypes)
        : getFilteredTankContents(getTankContentsInPortCall(stowagePlan, selectedPortCall, tank), operationTypes);
};

const getEmptyTankContent = (portCall: RotationPortCall | null, berthCall: RotationBerthCall | null, tank: Tank): TankContent => {
    return { portCall: portCall, berthCall: berthCall, tank: tank, cargoContents: [] };
};

export const getTankContentForCondition = (
    stowagePlan: StowagePlan,
    selectedPortCall: RotationPortCall,
    selectedBerthCall: RotationBerthCall,
    selectedTankPlanCondition: TankPlanCondition,
    tank: Tank
): TankContent => {
    const berthCallForSelection = getBerthCallForSelection(stowagePlan, selectedPortCall, selectedBerthCall);
    if (!berthCallForSelection) {
        return getEmptyTankContent(selectedPortCall, null, tank);
    }

    let tankContent: TankContent | null = null;

    // Handle Arrival condition separately due to its complexity
    if (selectedTankPlanCondition === TankPlanCondition.Arrival) {
        const previousBerthCall = selectedBerthCall
            ? getPreviousBerthCall(stowagePlan, berthCallForSelection)
            : getLastBerthCallInPortCall(getPreviousPortCall(stowagePlan, selectedPortCall)) ??
            getLastBerthCallFromPrecedingPortCalls(stowagePlan, selectedPortCall);

        if (previousBerthCall) {
            tankContent = getTankContentInBerthCall(stowagePlan, previousBerthCall, tank);
            if (tankContent?.cargoContents?.length) {
                // For Arrival, only include cargo contents with positive weights
                // Use direct comparison instead of isPositive to handle weights < 1 MT
                tankContent = {
                    ...tankContent,
                    cargoContents: tankContent.cargoContents.filter((c) => c.weight.gt(new Decimal(0))),
                };
            }
        }
        return tankContent ?? getEmptyTankContent(selectedPortCall, berthCallForSelection, tank);
    }

    // Handle other conditions with direct mapping
    const operationTypeMap: Record<TankPlanCondition, string[] | null> = {
        [TankPlanCondition.Departure]: null,
        [TankPlanCondition.OperationsInCall]: ['Loading', 'Unloading'],
        [TankPlanCondition.LoadingOperations]: ['Loading'],
        [TankPlanCondition.UnloadingOperations]: ['Unloading'],
        [TankPlanCondition.TransitOperations]: ['Transit'],
        [TankPlanCondition.Arrival]: [],
    };

    const operationTypes = operationTypeMap[selectedTankPlanCondition];
    tankContent = operationTypes
        ? getTankContentForOperationsInternal(stowagePlan, selectedBerthCall, selectedPortCall, tank, operationTypes)
        : getTankContentInBerthCall(stowagePlan, berthCallForSelection, tank);

    return tankContent ?? getEmptyTankContent(selectedPortCall, berthCallForSelection, tank);
};

export const getCargoContentForCondition = (
    stowagePlan: StowagePlan,
    selectedPortCall: RotationPortCall,
    selectedBerthCall: RotationBerthCall,
    selectedTankPlanCondition: TankPlanCondition
): CargoContent[] => {
    const berthCallForSelection = getBerthCallForSelection(stowagePlan, selectedPortCall, selectedBerthCall);
    if (!berthCallForSelection) {
        // TODO: RETURN-TYPE - Function can return undefined when berthCallForSelection is falsy, but return type is CargoContent[] (not nullable). Should return empty array [] instead of undefined.
        return [];
    }

    let cargoContents: CargoContent[] = [];

    // Handle Arrival condition separately due to its complexity
    if (selectedTankPlanCondition === TankPlanCondition.Arrival) {
        const previousBerthCall = selectedBerthCall
            ? getPreviousBerthCall(stowagePlan, berthCallForSelection)
            : getLastBerthCallInPortCall(getPreviousPortCall(stowagePlan, selectedPortCall)) ??
            getLastBerthCallFromPrecedingPortCalls(stowagePlan, selectedPortCall);

        if (previousBerthCall) {
            const cargoContentsInBerthCall = getCargoContentsInBerthCall(stowagePlan, previousBerthCall);
            if (cargoContents.length) {
                // For Arrival, only include cargo contents with positive weights
                cargoContents = cargoContentsInBerthCall.filter((c) => isPositive(c.weight));
            }
        }
        return cargoContents;
    }

    // Handle other conditions with direct mapping
    const operationTypeMap: Record<TankPlanCondition, string[] | null> = {
        [TankPlanCondition.Departure]: null,
        [TankPlanCondition.OperationsInCall]: ['Loading', 'Unloading'],
        [TankPlanCondition.LoadingOperations]: ['Loading'],
        [TankPlanCondition.UnloadingOperations]: ['Unloading'],
        [TankPlanCondition.TransitOperations]: ['Transit'],
        [TankPlanCondition.Arrival]: [],
    };

    const operationTypes = operationTypeMap[selectedTankPlanCondition];
    return operationTypes
        ? getCargoContentForOperationsInternal(stowagePlan, selectedBerthCall, selectedPortCall, operationTypes)
        : getCargoContentsInBerthCall(stowagePlan, berthCallForSelection);
};

const getCargoContentForOperationsInternal = (
    stowagePlan: StowagePlan,
    selectedBerthCall: RotationBerthCall,
    selectedPortCall: RotationPortCall,
    operationTypes: string[]
) => {
    return selectedBerthCall
        ? getFilteredCargoContents(getCargoContentsInBerthCall(stowagePlan, selectedBerthCall), operationTypes)
        : getFilteredCargoContents(getCargoContentsInPortCall(stowagePlan, selectedPortCall), operationTypes);
};
